import atlas from "azure-maps-control";
import * as atlasRest from "azure-maps-rest";
import {useEffect, useRef} from "react";
import "azure-maps-control/dist/atlas.min.css";
import {AzureMapPerCitySchema} from "../../api/schema";
import {normalizeProductLabel, PRODUCT_COLORS_BUBBLE, PRODUCT_COLORS_LABEL} from "../helper";

interface AzureMapPerCityProps {
    cities: AzureMapPerCitySchema[];
    isLoading?: boolean;
}

const AZURE_MAPS_KEY = process.env.REACT_APP_AZURE_MAPS_KEY;

const AzureMap = ({cities, isLoading}: AzureMapPerCityProps) => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const activePopupRef = useRef<atlas.Popup | null>(null);
    const activePopupKeyRef = useRef<string | null>(null);
    const suppressNextMapClickRef = useRef(false);

    const coordinateCache = useRef<Record<string, [number, number]>>({});
    const PH_BOUNDS: atlas.data.BoundingBox = [116.0, 4.5, 127.0, 21.5];

    const resolvePHLocationQuery = (cityName: string) => {
        const name = cityName.trim().toLowerCase();

        if (name === "quezon") {
            return "Quezon Province, Philippines";
        }

        if (name === "quezon city" || name === "qc") {
            return "Quezon City, Metro Manila, Philippines";
        }

        return `${cityName}, Philippines`;
    };

    useEffect(() => {
        if (!mapRef.current || isLoading) return;

        const map = new atlas.Map(mapRef.current, {
            center: [121.774, 12.8797],
            zoom: 4.5,
            minZoom: 4,
            maxZoom: 20,
            view: "Auto",
            interactive: true,
            showLogo: false,
            showFeedbackLink: false,
            maxBounds: PH_BOUNDS,
            authOptions: {
                authType: atlas.AuthenticationType.subscriptionKey,
                subscriptionKey: AZURE_MAPS_KEY,
            },
        });

        let animationActive = true;

        map.events.add("ready", async () => {
            map.resize();

            const dataSource = new atlas.source.DataSource();
            map.sources.add(dataSource);

            const pipeline = atlasRest.MapsURL.newPipeline(
                new atlasRest.SubscriptionKeyCredential(AZURE_MAPS_KEY ?? "")
            );

            const searchClient = new atlasRest.SearchURL(pipeline);

            for (const city of cities) {
                if (city.totalValues <= 0) continue;

                const query = resolvePHLocationQuery(city.cityName);
                const cacheKey = query;

                let coords = coordinateCache.current[cacheKey];

                if (!coords) {
                    try {
                        const response = await searchClient.searchFuzzy(atlasRest.Aborter.timeout(3000), query, {
                            limit: 1,
                            countrySet: ["PH"],
                        });

                        const result = response.results?.[0];
                        if (!result?.position) continue;

                        const {lon, lat} = result.position;
                        if (typeof lon !== "number" || typeof lat !== "number") continue;

                        coords = [lon, lat];
                        coordinateCache.current[city.cityName] = coords;
                    } catch {
                        continue;
                    }
                }

                const uniqueProducts = new Set(city.distribution.map((d) => normalizeProductLabel(d.label)));

                const color =
                    uniqueProducts.size > 1
                        ? PRODUCT_COLORS_BUBBLE.MULTIPLE
                        : PRODUCT_COLORS_BUBBLE[Array.from(uniqueProducts)[0]] ?? PRODUCT_COLORS_BUBBLE.MULTIPLE;
                dataSource.add(
                    new atlas.data.Feature(new atlas.data.Point(coords), {
                        cityName: city.cityName,
                        facilities: city.totalValues,
                        distribution: city.distribution,
                        color,
                        isSelected: false,
                    })
                );
            }

            const outerRing = new atlas.layer.BubbleLayer(dataSource, "outer-ring", {
                radius: [
                    "case",
                    ["get", "isSelected"],
                    ["interpolate", ["linear"], ["get", "facilities"], 1, 14, 50, 32],
                    0,
                ],

                color: ["get", "color"],
                strokeColor: "#ffffff00",

                opacity: ["case", ["get", "isSelected"], 0.35, 0],
            });

            const innerCircle = new atlas.layer.BubbleLayer(dataSource, "inner-circle", {
                radius: ["interpolate", ["linear"], ["get", "facilities"], 1, 6, 5, 8, 10, 10, 25, 14, 50, 18],
                color: ["get", "color"],
                strokeColor: "#ffffff",
                strokeWidth: 3,
            });

            const textLayer = new atlas.layer.SymbolLayer(dataSource, "count-text", {
                iconOptions: {
                    image: "",
                    allowOverlap: true,
                },
                textOptions: {
                    textField: ["case", ["<=", ["get", "facilities"], 1], "", ["to-string", ["get", "facilities"]]],
                    size: ["interpolate", ["linear"], ["get", "facilities"], 2, 11, 10, 13, 50, 16],
                    color: "#ffffff",
                    haloColor: "rgba(0,0,0,0.35)",
                    haloWidth: 1,
                    anchor: "center",
                    allowOverlap: true,
                    font: ["SegoeUi-Bold"],
                },
            });

            map.layers.add([outerRing, innerCircle, textLayer]);

            let pulse = 0;
            let direction = 1;

            const animateSelectedRing = () => {
                pulse += 0.015 * direction;
                if (pulse >= 1) direction = -1;
                if (pulse <= 0) direction = 1;

                outerRing.setOptions({
                    radius: [
                        "case",
                        ["get", "isSelected"],
                        ["interpolate", ["linear"], ["get", "facilities"], 1, 14 + pulse * 6, 50, 32 + pulse * 10],
                        0,
                    ],
                });

                requestAnimationFrame(animateSelectedRing);
            };

            animateSelectedRing();

            map.events.add("click", innerCircle, (e) => {
                if (!e.shapes?.length || !e.position) return;

                suppressNextMapClickRef.current = true;
                setTimeout(() => {
                    suppressNextMapClickRef.current = false;
                }, 0);

                const shape = e.shapes[0];
                if (!("getProperties" in shape) || !("setProperties" in shape)) return;

                const props = shape.getProperties();
                const popupKey = String(props.cityName);
                const wasSelected = props.isSelected === true;

                // 🔄 Clear selection on ALL bubbles
                dataSource.getShapes().forEach((s) => {
                    if ("setProperties" in s) {
                        s.setProperties({
                            ...s.getProperties(),
                            isSelected: false,
                        });
                    }
                });

                // ❌ SAME bubble clicked → close popup & stop
                if (activePopupRef.current && activePopupKeyRef.current === popupKey) {
                    activePopupRef.current.close();
                    activePopupRef.current = null;
                    activePopupKeyRef.current = null;
                    return;
                }

                // ❌ Close any existing popup
                if (activePopupRef.current) {
                    activePopupRef.current.close();
                    activePopupRef.current = null;
                    activePopupKeyRef.current = null;
                }

                // ✅ Select this bubble (ONLY if it wasn’t already selected)
                if (!wasSelected) {
                    shape.setProperties({
                        ...props,
                        isSelected: true,
                    });
                }

                const distribution = props.distribution as {
                    label: string;
                    value: number;
                }[];

                const isSingleDistribution = distribution.length === 1;

                const breakdownHtml = distribution
                .map((item) => {
                    const [productPart, facilityPart] = item.label.split(":");

                    const productKey = normalizeProductLabel(productPart?.trim());

                    return `
      <div style="display:flex;align-items:center;margin-top:6px;">
        
        <span style="
          color:${PRODUCT_COLORS_LABEL[productKey]};
          font-weight:600;
        ">
          ${productPart}${facilityPart ? ":" : ""}
        </span>

        ${
            facilityPart
                ? `
            <span style="
              color:#e5e7eb;
              font-weight:500;
              margin-left:6px;
            ">
              ${facilityPart.trim()}
            </span>
          `
                : ""
        }

        ${
            !isSingleDistribution
                ? `
            <span style="
              color:#e5e7eb;
              font-weight:600;
              margin-left:6px;
            ">
              ${item.value} facilities
            </span>
          `
                : ""
        }

      </div>
    `;
                })
                .join("");

                const popupId = `popup-close-${Date.now()}`;

                const popup = new atlas.Popup({
                    closeButton: false,
                    showPointer: false,
                    fillColor: "transparent",
                    pixelOffset: [0, -12],
                    position: e.position,
                    content: `
      <div style="
        position: relative;
        min-width:220px;
        padding:14px 44px 14px 16px;
        background:#1b2434;
        border-radius:14px;
        color:#fff;
        font-family:Inter,system-ui,sans-serif;
        box-shadow:0 14px 40px rgba(0,0,0,0.55);
      ">
        <button
          id="${popupId}"
          style="
            position:absolute;
            top:10px;
            right:10px;
            width:20px;
            height:20px;
            border-radius:8px;
            background:rgba(255,255,255,0.08);
            border:1px solid rgba(255,255,255,0.18);
            color:#ffffff;
            font-size:15px;
            cursor:pointer;
          "
        >
          ×
        </button>

        <div style="font-size:18px;font-weight:700;margin-bottom:6px;">
          ${props.cityName}
        </div>

        ${breakdownHtml}
      </div>
    `,
                });

                popup.open(map);

                requestAnimationFrame(() => {
                    document.getElementById(popupId)?.addEventListener("click", () => {
                        popup.close();
                        activePopupRef.current = null;
                        activePopupKeyRef.current = null;

                        // clear selection on close
                        dataSource.getShapes().forEach((s) => {
                            if ("setProperties" in s) {
                                s.setProperties({
                                    ...s.getProperties(),
                                    isSelected: false,
                                });
                            }
                        });
                    });
                });

                activePopupRef.current = popup;
                activePopupKeyRef.current = popupKey;
            });

            map.events.add("click", () => {
                if (suppressNextMapClickRef.current) return;

                dataSource.getShapes().forEach((s) => {
                    if ("setProperties" in s) {
                        s.setProperties({...s.getProperties(), isSelected: false});
                    }
                });

                if (activePopupRef.current) {
                    activePopupRef.current.close();
                    activePopupRef.current = null;
                    activePopupKeyRef.current = null;
                }
            });
        });

        return () => map.dispose();
    }, [cities, isLoading]);

    return (
        <div
            ref={mapRef}
            style={{
                width: "100%",
                height: "100%",
                borderRadius: 12,
                pointerEvents: "auto",
            }}
        />
    );
};

export default AzureMap;
